import {createContext, useCallback, useContext, useEffect, useMemo, useRef, type ReactNode} from 'react'
import type {ConcreteRequest} from 'relay-runtime'
import {DEFAULT_RELAY_CACHE_TTL, fetchQueryWithCache} from '../helpers'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {useRelayEnvironment} from 'react-relay'

type RelayCachePreheatContextType = {
  markQueryForPreheating: (query: ConcreteRequest, variables: Record<string, unknown>, source: string) => void
  preHeatQueries: () => void
}

type RelayCachePreheatContextProviderType = {
  children: ReactNode
}

const RelayCachePreheatContext = createContext<RelayCachePreheatContextType | null>(null)

export function RelayCachePreheatContextProvider({children}: RelayCachePreheatContextProviderType) {
  const relayEnvironment = useRelayEnvironment()
  const queriesToPreheatRef = useRef<
    Record<string, {query: ConcreteRequest; variables: Record<string, unknown>; source: string}>
  >({})
  const keysRef = useRef<Set<string>>(new Set())
  const preheatedRef = useRef<Set<string>>(new Set())

  const markQueryForPreheating = useCallback(
    (query: ConcreteRequest, variables: Record<string, unknown>, source: string) => {
      const queryName = query.fragment.name
      // generate a key from the query name and sorted variables to avoid duplicates
      const key = `${queryName}#${JSON.stringify(
        Object.fromEntries(Object.entries(variables).sort(([a], [b]) => (a < b ? -1 : 1))),
      )}`
      if (!keysRef.current.has(key)) {
        queriesToPreheatRef.current[key] = {query, variables, source}
        keysRef.current.add(key)
      }
    },
    [],
  )

  const preHeatQueries = useCallback(() => {
    for (const key of keysRef.current) {
      if (!preheatedRef.current.has(key)) {
        const queryToPreheat = queriesToPreheatRef.current[key]
        if (queryToPreheat) {
          fetchQueryWithCache(
            relayEnvironment,
            queryToPreheat.query,
            queryToPreheat.variables,
            {fetchPolicy: 'store-or-network'},
            DEFAULT_RELAY_CACHE_TTL,
            queryToPreheat.source,
          ).toPromise()
          preheatedRef.current.add(key)
        }
      }
    }
  }, [relayEnvironment])

  useEffect(() => {
    let cancelled = false
    let timeoutId: number | undefined

    const scheduleNextPreheat = () => {
      if (cancelled) return

      if (!('requestIdleCallback' in window)) {
        return
      }
      // eslint-disable-next-line compat/compat
      requestIdleCallback(() => {
        preHeatQueries()
        // Schedule next check after a 1 second delay
        timeoutId = window.setTimeout(() => scheduleNextPreheat(), 5000)
      })
    }

    scheduleNextPreheat()

    return () => {
      cancelled = true
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId)
      }
    }
  }, [preHeatQueries])

  const contextValue: RelayCachePreheatContextType = useMemo(() => {
    return {
      markQueryForPreheating,
      preHeatQueries,
    }
  }, [markQueryForPreheating, preHeatQueries])

  return <RelayCachePreheatContext.Provider value={contextValue}>{children}</RelayCachePreheatContext.Provider>
}

export const useRelayCachePreheatContext = () => {
  const context = useContext(RelayCachePreheatContext)
  if (!context) {
    throw new Error('useRelayCachePreheatContext must be used within a RelayCachePreheatContextProvider.')
  }

  return context
}

try{ RelayCachePreheatContext.displayName ||= 'RelayCachePreheatContext' } catch {}
try{ RelayCachePreheatContextProvider.displayName ||= 'RelayCachePreheatContextProvider' } catch {}